import React, { useEffect, useState } from "react";
import {
  Card,
  List,
  Tag,
  Row,
  Col,
  Spin,
  Empty,
  Divider,
  Typography,
  Button,
  Space,
  message,
} from "antd";
import {
  CalendarOutlined,
  EnvironmentOutlined,
  HeartOutlined,
  CheckCircleOutlined,
  CloseOutlined,
} from "@ant-design/icons";

import "../LandingPage.css";

const { Title, Paragraph } = Typography;

/* -------------------------
   Utility functions
   ------------------------- */

const parseDateIso = (iso) => (iso ? new Date(iso) : null);
const isUpcoming = (dt) => (dt ? dt >= new Date() : true);
const formatDisplayDate = (dt) =>
  dt
    ? dt.toLocaleString("en-IN", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "";

/* -------------------------
   Main Component
   ------------------------- */

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [attendeesMap, setAttendeesMap] = useState({});
  const [loadingSummary, setLoadingSummary] = useState({});
  const [error, setError] = useState(null);

  // Fetch all events
  useEffect(() => {
    let mounted = true;
    setLoadingEvents(true);
    setError(null);

    fetch("/api/events")
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch events (${res.status})`);
        return res.json();
      })
      .then((data) => {
        if (!mounted) return;
        setEvents(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error("Error fetching events:", err);
        if (mounted) setError("Unable to load events. Please try again later.");
      })
      .finally(() => {
        if (mounted) setLoadingEvents(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  // Fetch attendee summary for an event
  async function fetchAttendeeSummary(eventId) {
    setLoadingSummary((s) => ({ ...s, [eventId]: true }));
    try {
      const res = await fetch(`/api/events/${eventId}/attendees-summary`);
      if (!res.ok) throw new Error(`Summary fetch failed (${res.status})`);
      const json = await res.json();
      setAttendeesMap((m) => ({ ...m, [eventId]: json }));
    } catch (err) {
      console.error("Error fetching attendees summary for event:", eventId, err);
      setAttendeesMap((m) => ({
        ...m,
        [eventId]: { interested: 0, going: 0, not_going: 0, my_status: null },
      }));
    } finally {
      setLoadingSummary((s) => ({ ...s, [eventId]: false }));
    }
  }

  // Fetch summaries after events load
  useEffect(() => {
    if (!events?.length) return;
    events.forEach((ev) => {
      if (!attendeesMap[ev.id]) fetchAttendeeSummary(ev.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  // Get auth token (for logged-in users)
  const getAuthToken = () => localStorage.getItem("user_token") || null;

  // Update RSVP status
  async function handleSetStatus(eventId, status) {
    const token = getAuthToken();
    if (!token) {
      message.info("Please login or register to RSVP for events.");
      window.location.href = "/register";
      return;
    }

    try {
      const res = await fetch("/api/event_attendees", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ event_id: eventId, status }),
      });

      if (res.status === 401) {
        message.warning("Session expired. Please login again.");
        window.location.href = "/register";
        return;
      }

      if (!res.ok) throw new Error(`RSVP failed (${res.status})`);

      const json = await res.json();
      setAttendeesMap((m) => ({ ...m, [eventId]: json }));
      message.success("Your RSVP has been updated!");
    } catch (err) {
      console.error("RSVP update failed:", err);
      message.error("Could not update RSVP. Please try again later.");
    }
  }

  // Render a single event card
  function renderEventCard(item) {
    const summary = attendeesMap[item.id] || {
      interested: 0,
      going: 0,
      not_going: 0,
      my_status: null,
    };
    const loading = !!loadingSummary[item.id];
    const startDate = parseDateIso(item.start_datetime);
    const upcoming = isUpcoming(startDate);

    return (
      <List.Item key={item.id}>
        <Card style={{ width: "100%" }} bordered hoverable>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={6} md={5}>
              <img
                src={item.image || "/images/globe.png"}
                alt={item.title}
                style={{
                  width: "100%",
                  height: 110,
                  objectFit: "cover",
                  borderRadius: 6,
                }}
              />
            </Col>

            <Col xs={24} sm={18} md={19}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  alignItems: "flex-start",
                }}
              >
                <div style={{ flex: 1 }}>
                  <Title level={4} style={{ margin: 0 }}>
                    {item.title}{" "}
                    <Tag color={upcoming ? "green" : "default"}>
                      {upcoming ? "Upcoming" : "Completed"}
                    </Tag>
                  </Title>

                  <Paragraph
                    style={{ margin: "6px 0 8px", color: "#4b5563" }}
                  >
                    {item.description}
                  </Paragraph>

                  <div
                    style={{
                      display: "flex",
                      gap: 16,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        alignItems: "center",
                        color: "#374151",
                      }}
                    >
                      <CalendarOutlined />{" "}
                      <span>{formatDisplayDate(startDate)}</span>
                    </div>

                    {item.location && (
                      <div
                        style={{
                          display: "flex",
                          gap: 6,
                          alignItems: "center",
                          color: "#374151",
                        }}
                      >
                        <EnvironmentOutlined />{" "}
                        <span>{item.location}</span>
                      </div>
                    )}

                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                        color: "#374151",
                      }}
                    >
                      {loading ? (
                        <Spin size="small" />
                      ) : (
                        <>
                          <Tag icon={<HeartOutlined />}>
                            {summary.interested} Interested
                          </Tag>
                          <Tag icon={<CheckCircleOutlined />} color="processing">
                            {summary.going} Going
                          </Tag>
                          <Tag icon={<CloseOutlined />}>
                            {summary.not_going} Not going
                          </Tag>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {upcoming && (
                  <Space direction="vertical">
                    <Button
                      type={
                        summary.my_status === "GOING" ? "primary" : "default"
                      }
                      onClick={() => handleSetStatus(item.id, "GOING")}
                      loading={loading}
                    >
                      Going
                    </Button>

                    <Button
                      type={
                        summary.my_status === "INTERESTED"
                          ? "primary"
                          : "dashed"
                      }
                      onClick={() => handleSetStatus(item.id, "INTERESTED")}
                      loading={loading}
                    >
                      Interested
                    </Button>

                    <Button
                      type={
                        summary.my_status === "NOT_GOING"
                          ? "primary"
                          : "default"
                      }
                      onClick={() => handleSetStatus(item.id, "NOT_GOING")}
                      loading={loading}
                    >
                      Not going
                    </Button>
                  </Space>
                )}
              </div>
            </Col>
          </Row>
        </Card>
      </List.Item>
    );
  }

  // Separate events into upcoming / completed
  const enriched = events.map((ev) => ({
    ...ev,
    _dateObj: parseDateIso(ev.start_datetime),
    _isUpcoming: isUpcoming(parseDateIso(ev.start_datetime)),
  }));

  const upcoming = enriched
    .filter((e) => e._isUpcoming)
    .sort((a, b) => a._dateObj - b._dateObj);

  const completed = enriched
    .filter((e) => !e._isUpcoming)
    .sort((a, b) => b._dateObj - a._dateObj);

  return (
    <div>
    

      <div style={{ maxWidth: 1100, margin: "28px auto", padding: "0 16px" }}>
        <Title level={2} style={{ textAlign: "center", marginBottom: 8 }}>
          Alumni Events
        </Title>

        <Paragraph
          style={{ textAlign: "center", marginBottom: 24, color: "#475569" }}
        >
          Stay updated with PSG IAS Alumni events. RSVP to participate in
          reunions, industry talks, and networking meets.
        </Paragraph>

        <Divider />

        {loadingEvents ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : error ? (
          <Empty description={error} />
        ) : events.length === 0 ? (
          <Empty description="No events to display" />
        ) : (
          <>
            {/* Upcoming */}
            <section style={{ marginBottom: 28 }}>
              <Title level={4}>Upcoming Events</Title>
              {upcoming.length === 0 ? (
                <Empty description="No upcoming events" />
              ) : (
                <List dataSource={upcoming} renderItem={renderEventCard} split />
              )}
            </section>

            <Divider />

            {/* Completed */}
            <section style={{ marginBottom: 28 }}>
              <Title level={4}>Completed Events</Title>
              {completed.length === 0 ? (
                <Empty description="No completed events yet" />
              ) : (
                <List
                  dataSource={completed}
                  renderItem={renderEventCard}
                  split
                />
              )}
            </section>
          </>
        )}
      </div>


    </div>
  );
}
